import { Box, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { FlashlightBenchmarkGrid } from '../../../components/FlashlightBenchmarkGrid';
import { calendarSchoolYearEnd, gridModel, onTrackChip, yearLabel, type GridYear, type SchemeLevel } from '../../../components/flashlightGrid';
import { getDistrictConfig } from '../../IndividualPlan/districtConfig';
import type { FlashlightAssignmentsResponse, FlashlightBenchmarksResponse } from '../../IndividualPlan/flashlightBenchmarks';
import { AccessHistoryChart } from './AccessHistoryChart';
import { accessHistoryOf, type AccessHistory, type ExitOutcome, type FirstMeasure } from './accessHistory';
import { AssignmentMonthChart } from './AssignmentMonthChart';
import { assignmentsModelOf } from './assignmentMonths';
import { languageSections, type LanguageSectionId } from './languageSections';
import { CARD_TEXT, Chip, ProfileCard } from './ProfileCard';
import type { ExitBar } from './AccessCard';
import type { StudentProfile } from '../studentProfileApi';
import { ROSTER } from '../tokens';

export type Read<T> = { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; value: T };

function settled<T extends { sources: { status: string }[] }>(read: Read<T>): Read<T> {
  if (read.kind === 'ready' && read.value.sources.some((source) => source.status === 'error' || source.status === 'skipped')) return { kind: 'failed' };
  return read;
}

const DEFAULT_SCHEME: SchemeLevel[] = [
  { score: 1, label: 'Limited', color: '#F86464' },
  { score: 2, label: 'Emerging', color: '#FBDA67' },
  { score: 3, label: 'Expanding', color: '#2BCC57' },
  { score: 4, label: 'Bridging', color: '#0094FF' },
  { score: 5, label: 'Advanced', color: '#7270FE' },
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const OUTAGE = (what: string) => `${what} could not be loaded. Refresh the page to try again.`;

function rubricKey(scheme: SchemeLevel[]): string {
  return [...scheme].sort((a, b) => a.score - b.score).map((level) => `${level.score}|${level.label}|${level.color}`).join(';');
}

const metaSx = { ...CARD_TEXT.lead, color: ROSTER.muted } as const;

function GroupHead({ title, meta, right }: { title: string; meta: string; right?: ReactNode }) {
  return (
    <Box data-testid="group-head" sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
      <Typography component="b" sx={{ ...CARD_TEXT.strong }}>{title}</Typography>
      <Typography component="span" sx={metaSx}>{meta}</Typography>
      {right}
    </Box>
  );
}

function Group({ id, gap = '16px', last, children }: { id: LanguageSectionId; gap?: string; last: boolean; children: ReactNode }) {
  return (
    <Box data-testid={`language-group-${id}`} sx={{ display: 'flex', flexDirection: 'column', gap, pb: last ? 0 : '24px', borderBottom: last ? 'none' : `1px solid ${ROSTER.border}` }}>
      {children}
    </Box>
  );
}

function FirstCell({ measure, thresholdLabel }: { measure: FirstMeasure; thresholdLabel: 'Exit' | 'Review' }) {
  return (
    <Box data-testid="access-first" sx={{ width: 176, display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 24 }}>
        <Typography component="b" sx={{ ...metaSx, fontWeight: 600 }}>{measure.label}</Typography>
        {measure.exit != null && <Typography component="span" sx={metaSx}>{`${thresholdLabel} ${measure.exit.toFixed(1)}`}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 32 }}>
        <Typography component="b" sx={{ fontFamily: ROSTER.font, fontSize: 24, lineHeight: '32px', fontWeight: 600, letterSpacing: '-0.5px', color: ROSTER.ink }}>{measure.value == null ? '–' : measure.value.toFixed(1)}</Typography>
        {measure.belowExit && <Chip tone="warn">Below exit</Chip>}
      </Box>
    </Box>
  );
}

function AccessGroup({ history, last }: { history: Exclude<AccessHistory, { kind: 'none' }>; last: boolean }) {
  if (history.kind === 'first') {
    return (
      <Group id="access" gap="20px" last={last}>
        <GroupHead title="WIDA ACCESS" meta={history.meta} />
        <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {history.measures.map((measure) => <FirstCell key={measure.key} measure={measure} thresholdLabel={history.thresholdLabel} />)}
        </Box>
        <Typography sx={CARD_TEXT.small}>{history.note}</Typography>
      </Group>
    );
  }
  return (
    <Group id="access" gap="20px" last={last}>
      <GroupHead title="WIDA ACCESS" meta={history.meta} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        {history.measures.map((measure) => <AccessHistoryChart key={measure.key} measure={measure} markersAfter={history.markers.map((m) => m.after)} thresholdLabel={history.thresholdLabel} />)}
      </Box>
      <Box data-testid="group-legend" sx={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap', ...metaSx }}>
        {history.legend.exit && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <svg width="20" height="4" aria-hidden="true"><line x1="0" y1="2" x2="20" y2="2" stroke={ROSTER.muted} strokeDasharray="4 3" /></svg>
            {history.legend.exit}
          </Box>
        )}
        {history.legend.markers.map((marker) => (
          <Box key={marker} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <svg width="4" height="16" aria-hidden="true"><line x1="2" y1="0" x2="2" y2="16" stroke="#C1C1CD" strokeDasharray="2 3" /></svg>
            {marker}
          </Box>
        ))}
        {history.legend.missing.map((entry) => <Box key={entry} component="span">{entry}</Box>)}
      </Box>
    </Group>
  );
}

function newestWindow(years: GridYear[]): string | null {
  const months = years
    .flatMap((year) => [...Object.values(year.windows ?? {}), ...year.benchmarks.flatMap((benchmark) => Object.values(benchmark.windows ?? {}))])
    .filter((m): m is string => Boolean(m));
  if (months.length === 0) return null;
  const [year, mm] = months.sort().pop()!.split('-');
  return `${MONTHS[Number(mm) - 1]} ${year}`;
}

function BenchmarksGroup({ read, thisYear, onOpenReport, last, usesFlashlight }: { read: Read<FlashlightBenchmarksResponse>; thisYear: number; onOpenReport: (() => void) | null; last: boolean; usesFlashlight: boolean }) {
  const years: GridYear[] = read.kind === 'ready' ? read.value.years : [];
  const model = gridModel(years, thisYear);
  const scheme = read.kind === 'ready' && read.value.scheme?.length ? read.value.scheme : DEFAULT_SCHEME;
  const span = `${yearLabel(thisYear - 1)} and ${yearLabel(thisYear)}`;
  const newest = model.hasData ? newestWindow(years.filter((y) => model.years.includes(y.schoolYear))) : null;
  const link = onOpenReport ? (
    <Button onClick={onOpenReport} sx={{ ml: 'auto', textTransform: 'none', fontFamily: ROSTER.font, fontWeight: 600, p: 0, minWidth: 0, lineHeight: '24px' }}>
      View progress report
    </Button>
  ) : null;
  if (!usesFlashlight) {
    return (
      <Group id="benchmarks" gap="24px" last={last}>
        <GroupHead title="Flashlight 360 benchmarks" meta="" right={link} />
      </Group>
    );
  }
  return (
    <Group id="benchmarks" gap="24px" last={last}>
      <GroupHead title="Flashlight 360 benchmarks" meta={newest ? `${span} · newest ${newest}` : span} right={link} />
      {read.kind === 'failed' && <Typography sx={CARD_TEXT.small}>{OUTAGE('Benchmarks')}</Typography>}
      {read.kind === 'ready' && !model.hasData && <Typography sx={metaSx}>No benchmarks scored yet.</Typography>}
      {read.kind === 'ready' && model.hasData && (
        <FlashlightBenchmarkGrid
          model={model}
          scheme={scheme}
          chipFor={(domain) => {
            const chip = onTrackChip(domain);
            return chip ? <Chip tone={chip.tone} title={chip.title}>{chip.label}</Chip> : null;
          }}
        />
      )}
    </Group>
  );
}

function AssignmentsGroup({ read: assignments, benchmarks, thisYear, now, last }: { read: Read<FlashlightAssignmentsResponse>; benchmarks: Read<FlashlightBenchmarksResponse>; thisYear: number; now: Date; last: boolean }) {
  const read: Read<FlashlightAssignmentsResponse> = benchmarks.kind === 'ready' ? assignments : benchmarks.kind === 'failed' ? { kind: 'failed' } : { kind: 'loading' };
  const years = read.kind === 'ready' ? read.value.years : [];
  const scheme = benchmarks.kind === 'ready' && benchmarks.value.scheme?.length ? benchmarks.value.scheme : DEFAULT_SCHEME;
  const scoredYear = benchmarks.kind === 'ready' ? benchmarks.value.years.find((y) => y.schoolYear === thisYear) : undefined;
  const benchmarkYear = scoredYear && (!scoredYear.scheme?.length || rubricKey(scoredYear.scheme) === rubricKey(scheme)) ? scoredYear : undefined;
  const model = read.kind === 'ready' ? assignmentsModelOf(years, thisYear, benchmarkYear, now) : null;
  return (
    <Group id="assignments" gap="24px" last={last}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <GroupHead title="Flashlight 360 assignments" meta={model?.kind === 'charts' ? model.meta : yearLabel(thisYear)} />
        {model?.kind === 'charts' && <Typography sx={CARD_TEXT.small}>{model.key}</Typography>}
      </Box>
      {read.kind === 'failed' && <Typography sx={CARD_TEXT.small}>{OUTAGE('Assignments')}</Typography>}
      {model?.kind === 'empty' && <Typography sx={metaSx}>{model.text}</Typography>}
      {model?.kind === 'charts' && (
        <>
          {model.domains.map((chart) => <AssignmentMonthChart key={chart.domain} chart={chart} months={model.months} scheme={scheme} />)}
          <Box data-testid="asg-counts" sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {model.counts.map((count) => (
              <Box key={count.label} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography component="span" sx={metaSx}>{count.label}</Typography>
                <Typography component="b" sx={CARD_TEXT.strong}>{count.text}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Group>
  );
}

export function LanguageOverTime({
  profile,
  bars,
  stateName,
  outcome = 'exits',
  benchmarks,
  assignments,
  onOpenReport,
  now = new Date(),
  usesFlashlight = getDistrictConfig().usesFlashlight,
}: {
  profile: StudentProfile;
  bars: ExitBar[] | null;
  stateName?: string;
  outcome?: ExitOutcome;
  benchmarks: Read<FlashlightBenchmarksResponse>;
  assignments: Read<FlashlightAssignmentsResponse>;
  onOpenReport: () => void;
  now?: Date;
  usesFlashlight?: boolean;
}) {
  const history = accessHistoryOf(profile, bars, stateName, outcome);
  const sections = languageSections({ accessKind: history.kind, usesFlashlight }).filter((s) => s.show);
  if (sections.length === 0) return null;
  const thisYear = calendarSchoolYearEnd(now);
  const lastId = sections[sections.length - 1]!.id;
  const benchmarksRead = settled(benchmarks);
  const assignmentsRead = settled(assignments);
  return (
    <ProfileCard title="Language over time" wide gap="24px">
      {sections.map(({ id }) => {
        const last = id === lastId;
        if (id === 'access' && history.kind !== 'none') return <AccessGroup key={id} history={history} last={last} />;
        if (id === 'benchmarks') return <BenchmarksGroup key={id} read={benchmarksRead} thisYear={thisYear} onOpenReport={profile.standing === 'departed' ? null : onOpenReport} last={last} usesFlashlight={usesFlashlight} />;
        if (id === 'assignments') return <AssignmentsGroup key={id} read={assignmentsRead} benchmarks={benchmarksRead} thisYear={thisYear} now={now} last={last} />;
        return null;
      })}
    </ProfileCard>
  );
}
